import { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { useHydra } from './hooks/useHydra';
import { useKalshiAccount } from './hooks/useKalshiAccount';
import { usePolyAccount } from './hooks/usePolyAccount';
import { playBootTone } from './lib/audio/defenseAudio';
import { readSoundSettings } from './lib/audio/soundSettings';
import { useAuthStore } from './stores/authStore';
import { LoginView } from './views/Login';
import { AdminDashboard } from './views/Admin';
import { ManagerDashboard } from './views/Manager';
import { AuthShell } from './components/layout/AuthShell';
import { ValueDashboard } from './views/ValueDashboard';
import { PositionTracker } from './views/PositionTracker';
import { Settings } from './views/Settings';
import { StreamRiftView } from './views/StreamRift';
import {
  TRADER_NAV_ITEMS,
  ADMIN_NAV_ITEMS,
  MANAGER_NAV_ITEMS,
  type TraderView,
  type AdminView,
  type ManagerView,
} from './types/navigation';
import { Loader2 } from 'lucide-react';

const G3View = lazy(() => import('./views/G3'));

function App() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const checkBootstrap = useAuthStore((s) => s.checkBootstrap);

  useEffect(() => {
    checkBootstrap();
  }, [checkBootstrap]);

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="bg-background text-muted-foreground flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (status === 'unauthenticated' || !user) {
    return <LoginView />;
  }

  switch (user.role) {
    case 'admin':
      return <AdminShell />;
    case 'manager':
      return <ManagerShell />;
    case 'trader':
      return <TraderShell />;
    default:
      return <LoginView />;
  }
}

// ── Shared trader view renderer ──────────────────────────────────

function TraderViews({ currentView }: { currentView: string }) {
  return (
    <>
      {currentView === 'dashboard' && <ValueDashboard />}
      {currentView === 'positions' && <PositionTracker />}
      {currentView === 'settings' && <Settings />}
      {currentView === 'streamrift' && <StreamRiftView />}
      {currentView === 'g3' && (
        <Suspense
          fallback={
            <div className="text-muted-foreground flex h-full items-center justify-center text-xs">
              Loading G3…
            </div>
          }
        >
          <G3View />
        </Suspense>
      )}
    </>
  );
}

function useBootSound() {
  const bootPlayed = useRef(false);
  useEffect(() => {
    if (!bootPlayed.current) {
      bootPlayed.current = true;
      const snd = readSoundSettings();
      if (snd.bootSoundEnabled) playBootTone();
    }
  }, []);
}

const TRADER_VIEW_IDS = new Set(TRADER_NAV_ITEMS.map((i) => i.id));

// ── Admin Shell (onion: admin + manager + trader) ────────────────

function AdminShell() {
  useHydra();
  useKalshiAccount();
  usePolyAccount();
  useBootSound();
  const [currentView, setCurrentView] = useState<AdminView>('admin-manage');

  return (
    <AuthShell
      navItems={ADMIN_NAV_ITEMS}
      currentView={currentView}
      onViewChange={setCurrentView}
      brandLetter="A"
    >
      {currentView === 'admin-manage' && <AdminDashboard />}
      {currentView === 'admin-traders' && <ManagerDashboard currentView="manager-traders" adminMode />}
      {TRADER_VIEW_IDS.has(currentView as TraderView) && (
        <TraderViews currentView={currentView} />
      )}
    </AuthShell>
  );
}

// ── Manager Shell (onion: manager + trader) ──────────────────────

function ManagerShell() {
  useHydra();
  useKalshiAccount();
  usePolyAccount();
  useBootSound();
  const [currentView, setCurrentView] = useState<ManagerView>('manager-traders');

  return (
    <AuthShell
      navItems={MANAGER_NAV_ITEMS}
      currentView={currentView}
      onViewChange={setCurrentView}
      brandLetter="M"
    >
      {currentView === 'manager-traders' && <ManagerDashboard currentView="manager-traders" />}
      {TRADER_VIEW_IDS.has(currentView as TraderView) && (
        <TraderViews currentView={currentView} />
      )}
    </AuthShell>
  );
}

// ── Trader Shell ─────────────────────────────────────────────────

function TraderShell() {
  useHydra();
  useKalshiAccount();
  usePolyAccount();
  useBootSound();
  const [currentView, setCurrentView] = useState<TraderView>('dashboard');

  return (
    <AuthShell
      navItems={TRADER_NAV_ITEMS}
      currentView={currentView}
      onViewChange={setCurrentView}
    >
      <TraderViews currentView={currentView} />
    </AuthShell>
  );
}

export default App;
